import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import appConfig from "../../appConfig";
import { fetchSecure, getToken } from "../../services/apiService";
import { canOpenComparison, isBomOutput, shortEnvironment } from "../bomScheduler/outputs";

const OPEN_KEY = "bomViewer.inputsPanel";
const KEEP = { compare: ["tab", "q", "show", "issues", "view", "type"], bom: ["tab", "q", "issues", "view", "type"] };

const inputsCache = new Map();

const readOpen = () => {
  try {
    const value = localStorage.getItem(OPEN_KEY);
    if (value) return value === "open";
  } catch {
    return window.innerWidth >= 1200;
  }
  return window.innerWidth >= 1200;
};

const saveOpen = (open) => {
  try {
    localStorage.setItem(OPEN_KEY, open ? "open" : "closed");
  } catch {
    return;
  }
};

const byId = (a, b) => a.id - b.id;

const targetFor = (input, current) => {
  const boms = input.outputs.filter(isBomOutput).sort(byId);
  if (current.kind === "compare" && canOpenComparison(input)) return { kind: "compare", id: input.id };
  const sameEnvironment = current.environment && boms.find((o) => o.environment === current.environment);
  const sameSide = current.side === "b" && boms.find((o) => o.environment === input.environmentB);
  const bom = sameEnvironment || sameSide || boms[0];
  return bom ? { kind: "bom", id: bom.id } : null;
};

const runLabel = (input) => {
  const first = shortEnvironment(input.environmentA) || "Dev";
  if (input.compareInputId) return `${first} vs previous run`;
  return input.environmentB ? `${first} vs ${shortEnvironment(input.environmentB) || input.environmentB}` : `${first} only`;
};

export function useTestSetNav({ uploadId, inputId, kind, environment }) {
  const { instance, accounts } = useMsal();
  const [inputs, setInputs] = useState(() => inputsCache.get(uploadId) || []);
  const [open, setOpenState] = useState(readOpen);
  const [params] = useSearchParams();

  useEffect(() => {
    if (!uploadId) return undefined;
    let cancelled = false;
    const load = async () => {
      const token = await getToken(instance, accounts);
      const response = token ? await fetchSecure(token, `${appConfig.apiUrl}inputs/${uploadId}`) : null;
      if (cancelled || response?.status !== "OK" || !Array.isArray(response.data)) return;
      const sorted = [...response.data].sort((a, b) => (Number(a.test) || 0) - (Number(b.test) || 0) || a.id - b.id);
      inputsCache.set(uploadId, sorted);
      setInputs(sorted);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId]);

  const items = useMemo(() => {
    const esoiCount = inputs.reduce((counts, i) => counts.set(i.configurationId, (counts.get(i.configurationId) || 0) + 1), new Map());
    const keep = new URLSearchParams();
    (KEEP[kind] || []).forEach((key) => params.get(key) && keep.set(key, params.get(key)));
    const search = keep.toString() ? `?${keep}` : "";
    const currentInput = inputs.find((i) => i.id === inputId);
    const side = currentInput && environment && environment === currentInput.environmentB ? "b" : "a";
    return inputs.map((input) => {
      const target = targetFor(input, { kind, environment, side });
      return {
        input,
        url: target ? `/${target.kind}/${target.id}${search}` : null,
        run: esoiCount.get(input.configurationId) > 1 ? runLabel(input) : null,
        current: input.id === inputId,
      };
    });
  }, [inputs, kind, environment, inputId, params]);

  const setOpen = (value) => {
    setOpenState(value);
    saveOpen(value);
  };

  return { items, open, setOpen, available: inputs.length > 1 };
}

const PanelIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <line x1="9" y1="4" x2="9" y2="20" />
    {open ? <polyline points="16 10 14 12 16 14" /> : <polyline points="14 10 16 12 14 14" />}
  </svg>
);

const statusText = (input) => (input.status === "Done" ? null : input.status);

export function TestSetPanel({ nav }) {
  const [query, setQuery] = useState("");
  if (!nav.available) return null;

  if (!nav.open) {
    return (
      <aside className="bom-inputs bom-inputs--closed card border-0 shadow-sm" aria-label="Inputs of this test set">
        <button
          type="button"
          className="bom-inputs__toggle"
          onClick={() => nav.setOpen(true)}
          aria-expanded="false"
          aria-controls="test-set-inputs"
          title="Show the inputs of this test set"
        >
          <PanelIcon open={false} />
          <span className="bom-inputs__rail-label">Inputs {nav.items.length}</span>
        </button>
      </aside>
    );
  }

  const q = query.trim().toLowerCase();
  const shown = nav.items.filter(
    ({ input }) =>
      !q ||
      (input.configurationId || "").toLowerCase().includes(q) ||
      String(input.test) === q ||
      `#${input.id}` === q ||
      (input.codeString || "").toLowerCase().includes(q)
  );

  return (
    <aside id="test-set-inputs" className="bom-inputs card border-0 shadow-sm" aria-label="Inputs of this test set">
      <div className="bom-inputs__head">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="fw-semibold">Inputs</span>
          <span className="bom-viewer__count">{nav.items.length}</span>
          <button
            type="button"
            className="bom-inputs__toggle ms-auto"
            onClick={() => nav.setOpen(false)}
            aria-expanded="true"
            aria-controls="test-set-inputs"
            aria-label="Hide the inputs of this test set"
            title="Hide"
          >
            <PanelIcon open />
          </button>
        </div>
        <input
          type="search"
          className="form-control form-control-sm"
          placeholder="ESOI, test or codestring"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Find an input"
        />
      </div>
      <ul className="bom-inputs__list">
        {shown.map(({ input, url, run, current }) => {
          const status = statusText(input);
          const content = (
            <>
              <span className="bom-inputs__name">
                <span className="bom-inputs__test">{input.test}</span>
                <span className="bom-inputs__esoi">{input.configurationId}</span>
              </span>
              {(run || status) && <span className="bom-inputs__run">{[run, status].filter(Boolean).join(" · ")}</span>}
            </>
          );
          return (
            <li key={input.id}>
              {url ? (
                <Link
                  to={url}
                  className={`bom-inputs__item${current ? " is-current" : ""}`}
                  aria-current={current ? "page" : undefined}
                  title={input.codeString || undefined}
                >
                  {content}
                </Link>
              ) : (
                <div className="bom-inputs__item is-disabled" title={input.status === "Failed" ? input.errorMessage || "Failed: no BOM to open" : "No BOM yet"}>
                  {content}
                </div>
              )}
            </li>
          );
        })}
        {shown.length === 0 && <li className="bom-inputs__empty">No input matches</li>}
      </ul>
    </aside>
  );
}
